<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useTheme } from 'vuetify'
import { ERouteNames } from '@/router/ERouteNames'
import { hexToRgb } from '@core/utils/colorConverter'
import { useChartColors } from '@/utils/chartColors'
import { deleteWidget } from '@/modules/dashboard/composables/deleteWidget'
import type { IPieChartInfoWidget } from '@/modules/dashboard/types/IPieChartInfoWidget'
import {
  getPpuMapperConfig,
  isPpuSpecificStatusKey,
} from '@/modules/improvements/utils/ppuStatusHelper'

interface Props {
  widgetData: IPieChartInfoWidget
  currentTarget?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const route = useRoute()
const vuetifyTheme = useTheme()
const { generateColors } = useChartColors()
const showMenu = ref(false)

// Словарь для перевода статусов
const STATUS_MAP: Record<string, string> = {
  new: 'Новое',
  passed: 'Передано',
  in_progress: 'В работе',
  recalled: 'Отозвано',
  returned: 'Возвращено',
  rejected: 'Отложено',
  cancelled: 'Отменено',
  completed: 'Завершено',
  waiting: 'Ожидает',
  complies: 'Соответствует',
  does_not_comply: 'Не соответствует',
  not_applicable: 'Не применим',
  accepted: 'Реализовано',
}

const isPpuWidget = computed(() => {
  if (props.currentTarget === 'suggestion')
    return true
  return props.widgetData.data.labels.some(
    label => typeof label === 'string' && isPpuSpecificStatusKey(label),
  )
})

const isMonitoringRoute = computed(() => route.name === ERouteNames.MONITORING)

const totalValue = computed(() => {
  return props.widgetData.data.datasets[0].data.reduce((acc, val) => acc + val, 0)
})

/**
 * Функция для получения человекочитаемого названия
 */
const getLabel = (key: string | boolean) => {
  if (typeof key === 'boolean') {
    return key ? 'Отработано' : 'Не отработано'
  }
  if (isPpuWidget.value) {
    const ppu = getPpuMapperConfig(key)
    if (ppu)
      return ppu.label
  }
  return STATUS_MAP[key] || key
}

const getSliceColor = (key: string | boolean, index: number, fallbackColors: string[]) => {
  if (isPpuWidget.value && typeof key === 'string') {
    const ppu = getPpuMapperConfig(key)
    if (ppu) {
      const themeColor = vuetifyTheme.current.value.colors[ppu.color]
      if (themeColor)
        return themeColor
    }
  }
  return fallbackColors[index]
}

// Формируем данные с расчетом процентов и маппингом имен
const seriesWithMeta = computed(() => {
  const data = props.widgetData.data.datasets[0].data
  const labels = props.widgetData.data.labels
  const dataset = props.widgetData.data.datasets[0]
  const generatedPalette = generateColors(labels.length)
  const colors = dataset.colors?.length
    ? dataset.colors
    : generatedPalette
  const total = totalValue.value

  const assignedColors = labels.map((key, i) => getSliceColor(key, i, colors))
  const colorCounts: Record<string, number> = {}
  for (const c of assignedColors) {
    colorCounts[c] = (colorCounts[c] || 0) + 1
  }

  return labels.map((key, i) => {
    const mappedColor = assignedColors[i]
    const finalColor = colorCounts[mappedColor] > 1 ? generatedPalette[i] : mappedColor

    return {
      key,
      label: getLabel(key),
      value: data[i],
      color: finalColor,
      icon: dataset.icon,
      percentage: total > 0 ? Math.round((data[i] / total) * 100) : 0,
    }
  })
})

const chartOptions = computed(() => {
  const themeColors = vuetifyTheme.current.value.colors
  const variableTheme = vuetifyTheme.current.value.variables

  return {
    chart: {
      type: 'donut',
      parentHeightOffset: 0,
    },
    // Маппим лейблы и для самого графика (тултипы и т.д.)
    labels: seriesWithMeta.value.map(s => s.label),
    colors: seriesWithMeta.value.map(s => s.color),
    stroke: { width: 0 },
    dataLabels: { enabled: false },
    legend: { show: false },
    plotOptions: {
      pie: {
        donut: {
          size: '75%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '13px',
              offsetY: 20,
              color: `rgba(${hexToRgb(themeColors['on-surface'])},${variableTheme['medium-emphasis-opacity']})`,
              formatter: (val: string) => {
                if (!val) return ''
                if (val.length > 14) {
                  return `${val.slice(0, 12)}...`
                }
                return val
              },
            },
            value: {
              offsetY: -15,
              fontWeight: 600,
              fontSize: '24px',
              color: `rgba(${hexToRgb(themeColors['on-surface'])},${variableTheme['high-emphasis-opacity']})`,
            },
            total: {
              show: true,
              label: 'Всего',
              fontSize: '13px',
              color: `rgba(${hexToRgb(themeColors['on-surface'])},${variableTheme['medium-emphasis-opacity']})`,
              formatter: () => totalValue.value.toString(),
            },
          },
        },
      },
    },
  }
})

const chartSeries = computed(() => props.widgetData.data.datasets[0].data)
</script>

<template>
  <VCard class="pa-4 h-100 d-flex flex-column pie-chart-widget-card">
    <div class="d-flex align-center justify-space-between mb-4 flex-shrink-0">
      <div class="text-h5 d-flex align-center gap-2 min-w-0 text-truncate">
        <div
          v-show="widgetData.show_icon && widgetData.icon"
          class="chart-icon-wrapper flex-shrink-0"
          :style="{ background: widgetData.icon_background }"
        >
          <VIcon :icon="widgetData.icon" size="sm" />
        </div>
        <span class="text-truncate">{{ widgetData.name }}</span>
      </div>

      <VMenu v-if="isMonitoringRoute && !currentTarget" v-model="showMenu" :close-on-content-click="false">
        <template #activator="{ props: menuProps }">
          <VIconBtn v-bind="menuProps" variant="plain" icon="ri-more-2-line" />
        </template>
        <VCard>
          <VList>
            <VListItem prepend-icon="ri-delete-bin-3-line" @click="deleteWidget(widgetData.id)">
              <VListItemTitle>Удалить</VListItemTitle>
            </VListItem>
          </VList>
        </VCard>
      </VMenu>
    </div>

    <!-- Содержимое (обычное или загрузка) -->
    <div class="pie-chart-body flex-grow-1">
      <!-- Donut Chart или его скелетон -->
      <div class="pie-chart-container">
        <div v-if="loading" class="d-flex justify-center align-center" style="height: 200px;">
          <VSkeletonLoader
            type="avatar"
            class="custom-donut-skeleton bg-transparent"
          />
        </div>
        <VueApexCharts
          v-else
          type="donut"
          height="200"
          width="200"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>

      <!-- Легенда или ее скелетон -->
      <div class="pie-legend-container flex-grow-1">
        <div v-if="loading">
          <div v-for="i in 4" :key="i" class="d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center gap-2 w-75">
              <VSkeletonLoader type="avatar" style="width: 8px; height: 8px; min-width: 8px; min-height: 8px; border-radius: 50%;" class="bg-transparent" />
              <VSkeletonLoader type="text" width="60%" class="bg-transparent my-0" />
            </div>
            <VSkeletonLoader type="text" width="15%" class="bg-transparent my-0" />
          </div>
        </div>
        <div
          v-for="item in seriesWithMeta"
          v-else
          :key="item.key"
          class="legend-item d-flex align-start justify-space-between py-1 px-1 rounded gap-2"
        >
          <div class="d-flex align-start gap-2 min-w-0 flex-grow-1">
            <div
              :style="{ background: item.color }"
              class="rounded-circle flex-shrink-0 mt-1"
              style="width: 8px; height: 8px;"
            />
            <span class="text-body-2 status-label" :title="item.label">{{ item.label }}</span>
            <VIcon
              v-if="item.icon"
              :icon="item.icon"
              size="small"
              class="ml-1 flex-shrink-0 mt-0.5"
            />
          </div>
          <span class="font-weight-medium flex-shrink-0 text-caption text-high-emphasis ms-auto text-end" style="min-width: 36px;">{{ item.percentage }}%</span>
        </div>
      </div>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.pie-chart-widget-card {
  container-type: inline-size;
}

.pie-chart-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;

  @container (min-width: 380px) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 24px;
  }
}

.pie-chart-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  @container (min-width: 380px) {
    width: auto;
    min-width: 200px;
    max-width: 200px;
  }
}

.pie-legend-container {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @container (min-width: 380px) {
    flex: 1 1 0;
    width: auto;
  }
}

.status-label {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.35;
}

.legend-item {
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.04);
  }
}

.chart-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 4px;
}

.custom-donut-skeleton {
  width: 160px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  :deep(.v-skeleton-loader__avatar) {
    width: 160px !important;
    height: 160px !important;
    min-width: 160px !important;
    min-height: 160px !important;
    border-radius: 50% !important;
  }
}
</style>
